import { Checkbox, Form, message, Modal, Select } from 'antd';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import TextArea from 'antd/lib/input/TextArea';
import { stockLocationsRestCrudThunksForSupplier } from '../../thunks/stock_locations_thunks';
import { equipmentTypesRestCrudThunksForSupplier } from '../../thunks/equipment_types_thunks';
import {
	cancelScheduledAssetTransfer,
	scheduleAssetTransfer,
	updateScheduledAssetTransfer,
} from '../../thunks/assets_thunks';
import {
	bulkTransferAssetsToStockLocationsForSupplier,
	moveAssetToEquipmentPerStockLocationForSupplier,
} from '../../thunks/equipment_per_stock_locations_thunks';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import ModalFormFooter from '../common/ModalFormFooter';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { renderEquipmenTypeSelect } from '../purchase_order_form/ReplaceEquipmentLineItemModal';
import { StockLocationDropdownRecord } from '../StockLocationDropdownRecord';
import { DateTimeWithTimeZonev4 } from '../date_time_with_time_zone/DateTimeWithTimeZonev4';
import { wallTimeToUtcIso, BE_DATE_ONLY_FORMAT } from '../../utils/DataFormatterUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';

const SL_TC_NAME = 'scheduledTransferDropdown';
const ET_TC_NAME = 'scheduledTransferDropdown';
const FORM_ID = 'bulk-transfer-sl-form';

const BulkTransferToStockLocationModal: FC<any> = ({
	assetIds,
	transferId,
	initialValues,
	currentStockLocationId,
	onCancel,
	onSuccess,
	stockLocations,
	equipmentTypes,
	companyConfig,
	fetchStockLocations,
	fetchMultipleStockLocations,
	fetchEquipmentTypes,
	fetchMultipleEquipmentTypes,
	scheduleTransfer,
	updateTransfer,
	bulkTransferAssets,
	moveAssetToStockLocation,
	cancelTransfer,
}): React.ReactElement => {
	const [form] = Form.useForm();
	const [saving, setSaving] = useState(false);

	const transferNoteRequired = useMemo(
		() => nullSafeGetOrElse('inventoryConfig.transferNoteMandatory', companyConfig, false),
		[companyConfig]
	);

	const selectedStockLocationId = Form.useWatch('stockLocationId', form);
	const triggerAtValue = Form.useWatch('triggerAt', form);

	const selectedStockLocation = useMemo(() => {
		if (!selectedStockLocationId) return null;
		const records = getRecordsForTargetCollection(stockLocations, SL_TC_NAME);
		return records.find((_) => _.id === selectedStockLocationId) || null;
	}, [selectedStockLocationId, stockLocations]);

	const isFutureDate = useMemo(
		() => triggerAtValue && moment(triggerAtValue).isAfter(moment()),
		[triggerAtValue]
	);

	const handleSubmit = useCallback(
		(values) => {
			setSaving(true);

			if (!isFutureDate) {
				const transferDate = values.triggerAt
					? moment.utc(values.triggerAt).format(BE_DATE_ONLY_FORMAT)
					: undefined;
				let promise;
				if (assetIds.length === 1) {
					const data = {
						equipmentTypeId: values.equipmentTypeId,
						...(values.transferNote && { transferNote: values.transferNote }),
						...(transferDate && { transferDate }),
					};
					promise = moveAssetToStockLocation(assetIds[0], values.stockLocationId, data);
				} else {
					const entities = assetIds.map((assetId) => ({
						assetId: `${assetId}`,
						stockLocationId: values.stockLocationId,
						equipmentTypeId: values.equipmentTypeId,
						transferDate,
						...(values.transferNote && { transferNote: values.transferNote }),
					}));
					promise = bulkTransferAssets(entities);
				}

				promise
					.then(() => (transferId ? cancelTransfer({ assetIds }) : Promise.resolve()))
					.then(onSuccess)
					.catch(message.error)
					.finally(() => setSaving(false));
				return;
			}

			const triggerAt =
				values.triggerAt &&
				wallTimeToUtcIso(values.triggerAt, values.transferTimezone || moment.tz.guess());
			const entity = {
				triggerAt,
				assetIds,
				stockLocationId: values.stockLocationId,
				equipmentTypeId: values.equipmentTypeId,
				...(values.transferNote && { transferNote: values.transferNote }),
			};
			const promise = transferId ? updateTransfer(entity) : scheduleTransfer(entity);

			promise
				.then(onSuccess)
				.catch(message.error)
				.finally(() => setSaving(false));
		},
		[
			assetIds,
			isFutureDate,
			transferId,
			scheduleTransfer,
			updateTransfer,
			bulkTransferAssets,
			moveAssetToStockLocation,
			cancelTransfer,
			onSuccess,
		]
	);

	return (
		<Modal
			visible={true}
			title={transferId ? 'Edit scheduled transfer' : 'Transfer to stock location'}
			onCancel={onCancel}
			footer={null}
			closable={true}
		>
			<Form
				initialValues={{
					...initialValues,
					triggerAt: nullSafeGet('triggerAt', initialValues)
						? moment(initialValues.triggerAt).startOf('hour')
						: moment().startOf('hour'),
				}}
				id={FORM_ID}
				layout="vertical"
				form={form}
				onFinish={handleSubmit}
			>
				<Form.Item
					name="stockLocationId"
					label="Stock location"
					rules={[{ required: true, message: 'Stock location is required!' }]}
				>
					<OWAsyncSelect
						stateSlice={stockLocations}
						targetCollectionName={SL_TC_NAME}
						fetchMultiple={fetchMultipleStockLocations}
						fetchData={(searchText, targetCollectionName) =>
							fetchStockLocations({ name: searchText }, targetCollectionName)
						}
						renderRecord={(record) => (
							<Select.Option
								key={record.id}
								value={record.id}
								disabled={record.id === currentStockLocationId}
							>
								<StockLocationDropdownRecord record={record} />
							</Select.Option>
						)}
					/>
				</Form.Item>
				<Form.Item
					name="equipmentTypeId"
					label="Equipment Type"
					rules={[{ required: true, message: 'Equipment type is required!' }]}
				>
					<OWAsyncSelect
						stateSlice={equipmentTypes}
						targetCollectionName={ET_TC_NAME}
						fetchMultiple={fetchMultipleEquipmentTypes}
						fetchData={(searchText, targetCollectionName) =>
							fetchEquipmentTypes({ search: searchText }, targetCollectionName)
						}
						renderRecord={renderEquipmenTypeSelect}
					/>
				</Form.Item>

				<div style={{ marginBottom: 24 }}>
					<DateTimeWithTimeZonev4
						form={form}
						location={selectedStockLocation}
						displayName="Effective transfer date"
						dateName="triggerAt"
						timeZoneName="transferTimezone"
						rules={[{ required: true, message: 'Transfer date is required.' }]}
						help={
							isFutureDate ? (
								<span>Future-timed transfers will be executed on the chosen date and time.</span>
							) : null
						}
						showTime={{ format: 'h A', showMinute: false, showSecond: false }}
						required
						showNow={false}
					/>
				</div>

				<Form.Item
					name="transferNote"
					label="Transfer Note"
					rules={[{ required: transferNoteRequired, message: 'Please enter transfer note.' }]}
				>
					<TextArea autoSize={{ minRows: 3 }} />
				</Form.Item>

				{isFutureDate ? (
					<Form.Item
						name="consent"
						rules={[
							{
								validator: (_, value) =>
									value ? Promise.resolve() : Promise.reject(new Error('Please confirm')),
							},
						]}
						valuePropName="checked"
					>
						<Checkbox>
							<span className="text-sunsetOrange-8">
								I understand that this is a future-dated transfer and it will be executed on the
								chosen date and time.
							</span>
						</Checkbox>
					</Form.Item>
				) : null}

				<ModalFormFooter
					okText={isFutureDate ? 'Schedule Transfer' : 'Transfer'}
					cancelText="Cancel"
					loading={saving}
					formId={FORM_ID}
					onCancelClick={onCancel}
				/>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	stockLocations: state.stock_locations,
	equipmentTypes: state.equipment_types,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	fetchStockLocations: (params, targetCollectionName) =>
		dispatch(
			stockLocationsRestCrudThunksForSupplier.readLite(
				{ ...params, noIsActiveFilter: false },
				targetCollectionName
			)
		),
	fetchMultipleStockLocations: (ids, targetCollectionName) =>
		dispatch(stockLocationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchEquipmentTypes: (params, targetCollectionName) =>
		dispatch(equipmentTypesRestCrudThunksForSupplier.readLite(params, targetCollectionName)),
	fetchMultipleEquipmentTypes: (ids, targetCollectionName) =>
		dispatch(equipmentTypesRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	scheduleTransfer: (data) => dispatch(scheduleAssetTransfer(ROLE_TYPES.SUPPLIER)(data)),
	updateTransfer: (data) => dispatch(updateScheduledAssetTransfer(ROLE_TYPES.SUPPLIER)(data)),
	bulkTransferAssets: (entities) =>
		dispatch(bulkTransferAssetsToStockLocationsForSupplier(entities)),
	moveAssetToStockLocation: (assetId, toStockLocationId, data) =>
		dispatch(moveAssetToEquipmentPerStockLocationForSupplier(assetId, toStockLocationId, data)),
	cancelTransfer: (data) => dispatch(cancelScheduledAssetTransfer(ROLE_TYPES.SUPPLIER)(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BulkTransferToStockLocationModal);
